import React, { useEffect, useRef, useState } from 'react';
import API_BASE_URL from '../../config/api';

const ChatBotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('main');
  const [isMinimized, setIsMinimized] = useState(false);
  const [email, setEmail] = useState('');
  const [authMethod, setAuthMethod] = useState('email');

  // Backend State Integration
  const [conversationId, setConversationId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [showBackButton, setShowBackButton] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Guest ID
  const [guestId] = useState(() => {
    if (typeof window !== 'undefined') {
      let id = localStorage.getItem('chat_guest_id');
      if (!id) {
        id = 'guest_' + Math.random().toString(36).substr(2, 9) + Date.now();
        localStorage.setItem('chat_guest_id', id);
      }
      return id;
    }
    return null;
  });

  const assistantName =
    typeof document !== 'undefined' && document.title
      ? document.title
      : 'GK-Distribution';

  // --- Backend Sync Logic ---

  // Fetch active conversation
  useEffect(() => {
    if (isOpen) {
      const fetchActiveChat = async () => {
        try {
          const token = localStorage.getItem('token');
          const headers = { 'Content-Type': 'application/json' };
          if (token) headers['Authorization'] = `Bearer ${token}`;

          const res = await fetch(`${API_BASE_URL}/chat/active?guestId=${guestId}`, { headers });
          const json = await res.json();

          if (json.status === 'success' && json.data) {
            setConversationId(json.data._id);
            // Map backend messages to UI format - preserve admin role
            const mapped = json.data.messages.map(m => ({
              id: m._id || Date.now() + Math.random(),
              type: m.role === 'user' ? 'user' : m.role === 'admin' ? 'admin' : 'ai',
              message: m.content,
              role: m.role // Keep original role for reference
            }));
            setChatMessages(mapped);
            // If we have history, show it
            if (mapped.length > 0) {
              setShowBackButton(true);
            }
          }
        } catch (err) {
          console.error("Failed to fetch active chat", err);
        }
      };
      fetchActiveChat();
    }
  }, [isOpen, guestId]);

  // Polling for updates
  useEffect(() => {
    if (!isOpen || !conversationId) return;
    const poll = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_BASE_URL}/chat/${conversationId}?guestId=${guestId}`, { headers });
        const json = await res.json();
        if (json.status === 'success' && json.data) {
          const mapped = json.data.messages.map(m => ({
            id: m._id || Date.now() + Math.random(),
            type: m.role === 'user' ? 'user' : m.role === 'admin' ? 'admin' : 'ai',
            message: m.content,
            role: m.role
          }));
          if (mapped.length !== chatMessages.length) {
            setChatMessages(mapped);
            scrollToBottom(true);
          }
        }
      } catch (e) { }
    };
    const interval = setInterval(poll, 3000); // Faster polling for responsiveness
    return () => clearInterval(interval);
  }, [isOpen, conversationId, chatMessages.length, guestId]);


  // Auto-scroll
  const messagesEndRef = useRef(null);
  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      try {
        messagesEndRef.current.scrollIntoView({
          behavior: smooth ? 'smooth' : 'auto',
          block: 'end',
        });
      } catch (_) { }
    }
  };

  useEffect(() => {
    if (isOpen) scrollToBottom(false);
  }, [isOpen, currentScreen]);

  useEffect(() => {
    if (chatMessages.length) scrollToBottom(true);
  }, [chatMessages]);

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    setCurrentScreen('main');
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleTrackOrder = () => {
    setCurrentScreen('track-order');
  };

  const handleBack = () => {
    if (currentScreen === 'track-order' || currentScreen === 'history') {
      setCurrentScreen('main');
      // If we have messages, we are in a conversation, so show back button (to allow exit)
      // If no messages, we are at home, so no back button
      setShowBackButton(chatMessages.length > 0);
    } else if (showBackButton) {
      // User is in a conversation and wants to go back to Home
      setChatMessages([]);
      setConversationId(null);
      setCurrentScreen('main');
      setShowBackButton(false);
    }
  };

  const handleChatHistory = () => {
    setCurrentScreen('history');
    setShowBackButton(true);
  };

  // We are removing multi-conversation "New Chat" logic for now as persistence usually implies one active thread per guest/user
  // unless we build a full multi-thread UI. For now, "New Chat" just goes to main.
  const handleNewChat = () => {
    setCurrentScreen('main');
    setShowBackButton(true);
  };

  const handleSignIn = () => { window.location.href = '/login'; };
  const handleCantSignIn = () => { window.location.href = '/login'; };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;
    const msgText = inputMessage;

    // Ensure back button is shown when starting a chat manually
    setShowBackButton(true);

    // Optimistic UI
    const newUserMsg = { id: Date.now(), type: 'user', message: msgText };
    setChatMessages(prev => [...prev, newUserMsg]);
    setInputMessage('');
    setIsSending(true);
    scrollToBottom(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE_URL}/chat/message`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          conversationId,
          message: msgText,
          guestId
        })
      });
      const json = await res.json();

      if (json.status === 'success') {
        setConversationId(json.data.conversationId);
        const mapped = json.data.messages.map(m => ({
          id: m._id || Date.now() + Math.random(),
          type: m.role === 'user' ? 'user' : m.role === 'admin' ? 'admin' : 'ai',
          message: m.content,
          role: m.role
        }));
        setChatMessages(mapped);
      } else {
        throw new Error(json.message);
      }
    } catch (e) {
      console.error(e);
      setChatMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'ai',
        message: "Sorry, I'm having trouble connecting. Please try again."
      }]);
    } finally {
      setIsSending(false);
      scrollToBottom(true);
    }
  };

  const handleQuickAction = (action) => {
    const question = getQuickQuestion(action);
    setInputMessage(question);
    // Auto-send for quick actions
    sendDirectMessage(question);
    setShowBackButton(true); // Switch to chat view
  };

  const sendDirectMessage = async (text) => {
    setIsSending(true);
    const newUserMsg = { id: Date.now(), type: 'user', message: text };
    setChatMessages(prev => [...prev, newUserMsg]);
    scrollToBottom();

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE_URL}/chat/message`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ conversationId, message: text, guestId })
      });
      const json = await res.json();
      if (json.status === 'success') {
        setConversationId(json.data.conversationId);
        const mapped = json.data.messages.map(m => ({
          id: m._id || Date.now() + Math.random(),
          type: m.role === 'user' ? 'user' : m.role === 'admin' ? 'admin' : 'ai',
          message: m.content,
          role: m.role
        }));
        setChatMessages(mapped);
      }
    } catch (e) {
      setChatMessages(prev => [...prev, { id: Date.now(), type: 'ai', message: "Connection error." }]);
    } finally {
      setIsSending(false);
    }
  };

  const getQuickQuestion = (action) => {
    switch (action) {
      case 'track-order': return 'How can I track my order?';
      case 'report-issue': return 'I need to report an issue with my order';
      case 'other': return 'I have a general question';
      case 'cancel-order': return 'How can I cancel my order?';
      case 'product-question': return 'I have a question about your products';
      default: return 'How can you help me?';
    }
  };

  if (!isOpen) {
    return (
      <div className="chat-widget z-40 sm:z-50">
        <button
          onClick={handleOpen}
          className="w-14 h-14 bg-black rounded-full flex items-center justify-center shadow-lg hover:bg-gray-800 transition-colors"
        >
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="64" height="64" rx="32" fill="black" />
            <path d="M16.0013 45.9988L14.1414 45.2589C14.0085 45.5932 13.9683 45.9573 14.025 46.3126C14.0817 46.6679 14.2332 47.0014 14.4635 47.2778C14.6939 47.5542 14.9946 47.7634 15.3338 47.8832C15.6731 48.0031 16.0384 48.0292 16.3913 47.9587L16.0013 45.9988ZM25.413 44.1269L26.3609 42.365L25.7269 42.025L25.023 42.165L25.413 44.1269ZM19.2492 37.8331L21.1091 38.5731L21.4351 37.7451L21.0431 36.9472L19.2492 37.8331ZM46.0002 31.9994C46.0002 38.4911 40.2944 43.9989 32.9407 43.9989V47.9987C42.2223 47.9987 50 40.971 50 31.9994H46.0002ZM19.8832 31.9994C19.8832 25.5076 25.591 19.9998 32.9427 19.9998V16C23.661 16 15.8813 23.0277 15.8813 31.9994H19.8832ZM32.9427 19.9998C40.2944 19.9998 46.0002 25.5076 46.0002 31.9994H50C50 23.0277 42.2243 16 32.9427 16V19.9998ZM32.9407 43.9989C30.5308 43.9989 28.2868 43.3989 26.3609 42.365L24.465 45.8868C27.0715 47.2842 29.9832 48.01 32.9407 47.9987V43.9989ZM16.3913 47.9587L25.8029 46.0868L25.023 42.165L15.6113 44.0369L16.3913 47.9607V47.9587ZM21.0431 36.9472C20.2818 35.4087 19.8862 33.7159 19.8832 31.9994H15.8813C15.8813 34.3993 16.4453 36.6752 17.4533 38.7191L21.0431 36.9472ZM17.3913 37.0932L14.1414 45.2609L17.8573 46.7368L21.1051 38.5711L17.3893 37.0932H17.3913Z" fill="white" />
            <path d="M25.9999 33.9993C27.1044 33.9993 27.9998 33.1039 27.9998 31.9994C27.9998 30.8948 27.1044 29.9995 25.9999 29.9995C24.8954 29.9995 24 30.8948 24 31.9994C24 33.1039 24.8954 33.9993 25.9999 33.9993Z" fill="white" />
            <path d="M32.9999 33.9993C34.1044 33.9993 34.9998 33.1039 34.9998 31.9994C34.9998 30.8948 34.1044 29.9995 32.9999 29.9995C31.8954 29.9995 31 30.8948 31 31.9994C31 33.1039 31.8954 33.9993 32.9999 33.9993Z" fill="white" />
            <path d="M39.9999 33.9993C41.1044 33.9993 41.9998 33.1039 41.9998 31.9994C41.9998 30.8948 41.1044 29.9995 39.9999 29.9995C38.8954 29.9995 38 30.8948 38 31.9994C38 33.1039 38.8954 33.9993 39.9999 33.9993Z" fill="white" />
          </svg>
        </button>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <div className="chat-widget z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className="w-14 h-14 bg-black rounded-full flex items-center justify-center shadow-lg hover:bg-gray-800 transition-colors"
        >
          {/* Same Icon for Minimized */}
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="64" height="64" rx="32" fill="black" />
            <path d="M16.0013 45.9988L14.1414 45.2589C14.0085 45.5932 13.9683 45.9573 14.025 46.3126C14.0817 46.6679 14.2332 47.0014 14.4635 47.2778C14.6939 47.5542 14.9946 47.7634 15.3338 47.8832C15.6731 48.0031 16.0384 48.0292 16.3913 47.9587L16.0013 45.9988ZM25.413 44.1269L26.3609 42.365L25.7269 42.025L25.023 42.165L25.413 44.1269ZM19.2492 37.8331L21.1091 38.5731L21.4351 37.7451L21.0431 36.9472L19.2492 37.8331ZM46.0002 31.9994C46.0002 38.4911 40.2944 43.9989 32.9407 43.9989V47.9987C42.2223 47.9987 50 40.971 50 31.9994H46.0002ZM19.8832 31.9994C19.8832 25.5076 25.591 19.9998 32.9427 19.9998V16C23.661 16 15.8813 23.0277 15.8813 31.9994H19.8832ZM32.9427 19.9998C40.2944 19.9998 46.0002 25.5076 46.0002 31.9994H50C50 23.0277 42.2243 16 32.9427 16V19.9998ZM32.9407 43.9989C30.5308 43.9989 28.2868 43.3989 26.3609 42.365L24.465 45.8868C27.0715 47.2842 29.9832 48.01 32.9407 47.9987V43.9989ZM16.3913 47.9587L25.8029 46.0868L25.023 42.165L15.6113 44.0369L16.3913 47.9607V47.9587ZM21.0431 36.9472C20.2818 35.4087 19.8862 33.7159 19.8832 31.9994H15.8813C15.8813 34.3993 16.4453 36.6752 17.4533 38.7191L21.0431 36.9472ZM17.3913 37.0932L14.1414 45.2609L17.8573 46.7368L21.1051 38.5711L17.3893 37.0932H17.3913Z" fill="white" />
            <path d="M25.9999 33.9993C27.1044 33.9993 27.9998 33.1039 27.9998 31.9994C27.9998 30.8948 27.1044 29.9995 25.9999 29.9995C24.8954 29.9995 24 30.8948 24 31.9994C24 33.1039 24.8954 33.9993 25.9999 33.9993Z" fill="white" />
            <path d="M32.9999 33.9993C34.1044 33.9993 34.9998 33.1039 34.9998 31.9994C34.9998 30.8948 34.1044 29.9995 32.9999 29.9995C31.8954 29.9995 31 30.8948 31 31.9994C31 33.1039 31.8954 33.9993 32.9999 33.9993Z" fill="white" />
            <path d="M39.9999 33.9993C41.1044 33.9993 41.9998 33.1039 41.9998 31.9994C41.9998 30.8948 41.1044 29.9995 39.9999 29.9995C38.8954 29.9995 38 30.8948 38 31.9994C38 33.1039 38.8954 33.9993 39.9999 33.9993Z" fill="white" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="chat-widget z-50">
      <div className="w-[340px] sm:w-[455px] h-[530px] max-h-[80vh] sm:max-h-[90vh] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-black px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {(currentScreen !== 'main' || showBackButton) && (
              <button
                onClick={handleBack}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <div>
              <h3 className="text-white font-semibold text-sm">
                {currentScreen === 'track-order' ? 'Track my order' : assistantName}
              </h3>
              {currentScreen === 'main' && !showBackButton && (
                <p className="text-gray-300 text-xs">Available Now</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleChatHistory} className="text-white text-xs border border-white/30 rounded px-2 py-1 hover:bg-white/10">
              Chats
            </button>
            <button onClick={handleMinimize} className="text-white hover:text-gray-300 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 bg-white flex flex-col min-h-0">
          {currentScreen === 'main' && (
            <>
              {/* Messages Area - Show if there are messages OR if we want to show chat history view */}
              {chatMessages.length > 0 && (
                <div className="flex-1 overflow-y-auto space-y-3 mb-4 pr-1 min-h-0">
                  {chatMessages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs px-3 py-2 rounded-lg ${msg.type === 'user'
                        ? 'bg-black text-white'
                        : msg.type === 'admin'
                          ? 'bg-blue-600 text-white border-2 border-blue-700'
                          : 'bg-gray-100 text-gray-800'
                        }`}>
                        {msg.type === 'admin' && (
                          <p className="text-xs font-semibold mb-1 opacity-90">Admin</p>
                        )}
                        <p className="text-sm">{msg.message}</p>
                      </div>
                    </div>
                  ))}
                  {isSending && (
                    <div className="flex justify-start">
                      <div className="max-w-xs px-3 py-2 rounded-lg bg-gray-100 text-gray-800">
                        <p className="text-sm flex items-center gap-1">
                          <span>Typing</span>
                          <span className="inline-flex gap-1">
                            <span className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" />
                            <span className="w-1 h-1 bg-gray-500 rounded-full animate-bounce [animation-delay:.15s]" />
                            <span className="w-1 h-1 bg-gray-500 rounded-full animate-bounce [animation-delay:.3s]" />
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Initial Menu - Show whenever we have no messages */}
              {chatMessages.length === 0 && (
                <div className="flex-1 overflow-y-auto flex flex-col justify-start space-y-4 pt-4 min-h-0 pr-1">
                  {/* Track Orders Button */}
                  <button onClick={handleTrackOrder} className="w-full bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 7L4 7M20 7V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V7M20 7V5C20 4.46957 19.7893 3.96086 19.4142 3.58579C19.0391 3.21071 18.5304 3 18 3H6C5.46957 3 4.96086 3.21071 4.58579 3.58579C4.21071 3.96086 4 4.46957 4 5V7M8 11H16M8 15H12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <span className="text-gray-800 font-medium text-sm">Track and manage my orders</span>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18L15 12L9 6" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button onClick={handleChatHistory} className="w-full bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 text-xs font-medium">WRB</span>
                      </div>
                      <div className="text-left">
                        <p className="text-gray-800 font-medium text-sm">Chat History</p>
                        <p className="text-gray-500 text-xs">View previous conversations</p>
                      </div>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button onClick={() => handleQuickAction('track-order')} className="bg-white border border-gray-200 rounded-lg p-3 text-gray-800 text-sm hover:bg-gray-50 transition-colors">Track Order</button>
                    <button onClick={() => handleQuickAction('report-issue')} className="bg-white border border-gray-200 rounded-lg p-3 text-gray-800 text-sm hover:bg-gray-50 transition-colors">Report Issue</button>
                    <button onClick={() => handleQuickAction('other')} className="bg-white border border-gray-200 rounded-lg p-3 text-gray-800 text-sm hover:bg-gray-50 transition-colors">Other</button>
                    <button onClick={() => handleQuickAction('cancel-order')} className="bg-white border border-gray-200 rounded-lg p-3 text-gray-800 text-sm hover:bg-gray-50 transition-colors">Cancel Order</button>
                    <button onClick={() => handleQuickAction('product-question')} className="bg-white border border-gray-200 rounded-lg p-3 text-gray-800 text-sm hover:bg-gray-50 transition-colors col-span-2">Product Question</button>
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="flex items-center gap-2 mt-2 pt-2 border-t sticky bottom-0 bg-white">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Ask the chatbot anything you want"
                  className="flex-1 bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={isSending}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isSending ? 'bg-gray-400 cursor-not-allowed' : 'bg-black hover:bg-gray-800'}`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </>
          )}

          {currentScreen === 'history' && (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
              <p>History view is available in the main chat.</p>
              <button onClick={() => { setCurrentScreen('main'); setShowBackButton(chatMessages.length > 0); }} className="mt-2 text-blue-600 underline">Back to Chat</button>
            </div>
          )}

          {currentScreen === 'track-order' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto space-y-4 pr-1 min-h-0">
                <h2 className="text-xl font-bold text-gray-900">Sign in</h2>
                <div className="flex gap-2">
                  <label className="flex-1 cursor-pointer">
                    <input type="radio" name="authMethod" value="email" checked={authMethod === 'email'} onChange={(e) => setAuthMethod(e.target.value)} className="sr-only" />
                    <div className={`p-3 border rounded-lg text-center ${authMethod === 'email' ? 'border-black bg-white' : 'border-gray-200 bg-white'}`}>
                      <div className={`w-4 h-4 rounded-full border-2 mx-auto mb-2 ${authMethod === 'email' ? 'border-black bg-black' : 'border-gray-300'}`}></div>
                      <span className="text-sm font-medium">Email</span>
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer">
                    <input type="radio" name="authMethod" value="sms" checked={authMethod === 'sms'} onChange={(e) => setAuthMethod(e.target.value)} className="sr-only" />
                    <div className={`p-3 border rounded-lg text-center ${authMethod === 'sms' ? 'border-black bg-white' : 'border-gray-200 bg-white'}`}>
                      <div className={`w-4 h-4 rounded-full border-2 mx-auto mb-2 ${authMethod === 'sms' ? 'border-black bg-black' : 'border-gray-300'}`}></div>
                      <span className="text-sm font-medium">SMS</span>
                    </div>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your email*</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="space-y-4 mt-auto pt-3 border-t bg-white">
                <div className="text-sm"><span className="text-gray-600">Can't sign in? </span><button onClick={handleCantSignIn} className="text-blue-600 underline hover:text-blue-800">Send us a message</button></div>
                <button onClick={handleSignIn} className="w-full bg-black text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors">SIGN IN</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatBotWidget;
